import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import api from "../../lib/api.ts";
import { useLanguage } from "../../context/LanguageContext";
import { useCurrencyUtils } from "../../utils/currencyUtils";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";

export default function BuyerPackagePlans() {
  const { translate, language } = useLanguage();
  const isRTL = language === "ar";

  const { displayPriceWithSymbol, convertPrice } = useCurrencyUtils();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  const [packages, setPackages] = useState([]);
  const [startIndex, setStartIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState(0);

  const cardsPerPage = 4;

  // Modal states
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [receiptImage, setReceiptImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isRequesting, setIsRequesting] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showConfirmDialog) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showConfirmDialog]);

  // Fetch packages
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const res = await api.get("/buyerpackages");
        setPackages(res.data || []);
      } catch (err) {
        console.error("Failed to fetch buyer packages:", err);
        showError(
          translate("buyerPage.messages.loadPackagesFailed") ||
            "Failed to load packages"
        );
      }
    };
    fetchPackages();
  }, [translate, showError]);

  const handleLeft = () => {
    setSlideDirection(-1);
    setStartIndex((prev) => Math.max(prev - cardsPerPage, 0));
  };

  const handleRight = () => {
    setSlideDirection(1);
    setStartIndex((prev) =>
      Math.min(prev + cardsPerPage, packages.length - cardsPerPage)
    );
  };

  const handlePackageRequest = (pkg) => {
    if (!user) {
      showError(
        translate("buyerPage.messages.loginRequired") || "Please login first"
      );
      return;
    }
    setSelectedPackage(pkg);
    setReceiptImage(null);
    setImagePreview(null);
    setShowConfirmDialog(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReceiptImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target?.result);
    reader.readAsDataURL(file);
  };

  const closeConfirmDialog = () => {
    setShowConfirmDialog(false);
    setSelectedPackage(null);
    setReceiptImage(null);
    setImagePreview(null);
  };

  const confirmAndRequestPackage = async () => {
    if (!selectedPackage || !user || !receiptImage) return;

    try {
      setIsRequesting(true);
      const formData = new FormData();
      formData.append("buyerPackageId", String(selectedPackage.id));
      formData.append("userId", String(user.id));
      formData.append("receiptImage", receiptImage);

      const response = await api.post("/buyerpackagehistories", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data) {
        closeConfirmDialog();
        showSuccess(
          translate("buyerPage.messages.packageRequestSuccess") ||
            "Package request submitted successfully"
        );
      }
    } catch (err) {
      console.error("Error requesting package:", err);
      showError(
        err.response?.data?.message ||
          translate("buyerPage.messages.packageRequestFailed") ||
          "Failed to request package"
      );
    } finally {
      setIsRequesting(false);
    }
  };

  return (
    <section
      className="py-12 bg-primary-dark/40"
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      {/* Title */}
      <div className="text-center mb-12 px-4">
        <motion.h2
          className="text-3xl md:text-4xl font-bold text-white tracking-tight"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {translate("buyerPage.choosePlan")
            .split(" ")
            .map((word, i) => (
              <span key={i} className="text-white">
                {word}{" "}
              </span>
            ))}
        </motion.h2>

        <motion.p
          className="text-slate-400 mt-3 text-lg max-w-2xl mx-auto"
          initial={{ opacity: 0, y: -10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {translate("buyerPage.choosePlanSubtitle")}
        </motion.p>
      </div>

      {/* Cards Carousel */}
      <div className="relative max-w-7xl mx-auto px-4">
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          key={startIndex}
          initial={{ opacity: 0, x: slideDirection === 1 ? 100 : -100 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          onAnimationComplete={() => setSlideDirection(0)}
        >
          {packages.slice(startIndex, startIndex + cardsPerPage).map((pkg, i) => (
            <motion.div
              key={pkg.id}
              className="flex flex-col bg-page rounded-xl shadow-xl overflow-hidden border border-default hover:border-primary/50 transition-all duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              <div className="p-6 flex-1">
                <h3 className="font-bold text-xl text-white mb-5">{pkg.name}</h3>

                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="text-3xl font-bold text-primary">
                      {displayPriceWithSymbol(convertPrice(pkg.deposit || 0))}
                    </div>
                  </div>

                  <hr className="my-5 border-slate-700/50" />

                  <h4 className={`font-semibold text-lg mb-4 text-slate-200 ${isRTL ? "text-right" : "text-left"}`}>
                    {translate("buyerPage.features.heading")}
                  </h4>

                  <ul className={`space-y-3 text-slate-300 ${isRTL ? "space-y-reverse" : ""}`}>
                    <li className={`flex items-center gap-3 ${isRTL ? "flex-row-reverse" : ""}`}>
                      <svg width="20" height="20" viewBox="0 0 14 14" fill="none" className="flex-shrink-0">
                        <path
                          d="M7 0.5C5.71442 0.5 4.45772 0.881218 3.3888 1.59545C2.31988 2.30968 1.48676 3.32484 0.994786 4.51256C0.502816 5.70028 0.374095 7.00721 0.624899 8.26809C0.875703 9.52896 1.49477 10.6872 2.40381 11.5962C3.31285 12.5052 4.47104 13.1243 5.73192 13.3751C6.99279 13.6259 8.29973 13.4972 9.48744 13.0052C10.6752 12.5132 11.6903 11.6801 12.4046 10.6112C13.1188 9.54229 13.5 8.28558 13.5 7C13.4982 5.27665 12.8128 3.62441 11.5942 2.40582C10.3756 1.18722 8.72335 0.50182 7 0.5ZM9.85375 5.85375L6.35375 9.35375C6.30732 9.40024 6.25217 9.43712 6.19147 9.46228C6.13077 9.48744 6.06571 9.50039 6 9.50039C5.9343 9.50039 5.86923 9.48744 5.80853 9.46228C5.74783 9.43712 5.69269 9.40024 5.64625 9.35375L4.14625 7.85375C4.05243 7.75993 3.99972 7.63268 3.99972 7.5C3.99972 7.36732 4.05243 7.24007 4.14625 7.14625C4.24007 7.05243 4.36732 6.99972 4.5 6.99972C4.63268 6.99972 4.75993 7.05243 4.85375 7.14625L6 8.29313L9.14625 5.14625C9.19271 5.09979 9.24786 5.06294 9.30855 5.0378C9.36925 5.01266 9.43431 4.99972 9.5 4.99972C9.5657 4.99972 9.63075 5.01266 9.69145 5.0378C9.75215 5.06294 9.8073 5.09979 9.85375 5.14625C9.90021 5.1927 9.93706 5.24786 9.9622 5.30855C9.98734 5.36925 10.0003 5.4343 10.0003 5.5C10.0003 5.5657 9.98734 5.63075 9.9622 5.69145C9.93706 5.75214 9.90021 5.8073 9.85375 5.85375Z"
                          fill="#10b981"
                        />
                      </svg>
                      <span>
                        {translate("buyerPage.features.bidLimit")}{" "}
                        <strong className="text-white">
                          {displayPriceWithSymbol(convertPrice(pkg.creditLimit || 0))}
                        </strong>
                      </span>
                    </li>

                    <li className={`flex items-center gap-3 ${isRTL ? "flex-row-reverse" : ""}`}>
                      <svg width="20" height="20" viewBox="0 0 14 14" fill="none" className="flex-shrink-0">
                        <path
                          d="M7 0.5C5.71442 0.5 4.45772 0.881218 3.3888 1.59545C2.31988 2.30968 1.48676 3.32484 0.994786 4.51256C0.502816 5.70028 0.374095 7.00721 0.624899 8.26809C0.875703 9.52896 1.49477 10.6872 2.40381 11.5962C3.31285 12.5052 4.47104 13.1243 5.73192 13.3751C6.99279 13.6259 8.29973 13.4972 9.48744 13.0052C10.6752 12.5132 11.6903 11.6801 12.4046 10.6112C13.1188 9.54229 13.5 8.28558 13.5 7C13.4982 5.27665 12.8128 3.62441 11.5942 2.40582C10.3756 1.18722 8.72335 0.50182 7 0.5ZM9.85375 5.85375L6.35375 9.35375C6.30732 9.40024 6.25217 9.43712 6.19147 9.46228C6.13077 9.48744 6.06571 9.50039 6 9.50039C5.9343 9.50039 5.86923 9.48744 5.80853 9.46228C5.74783 9.43712 5.69269 9.40024 5.64625 9.35375L4.14625 7.85375C4.05243 7.75993 3.99972 7.63268 3.99972 7.5C3.99972 7.36732 4.05243 7.24007 4.14625 7.14625C4.24007 7.05243 4.36732 6.99972 4.5 6.99972C4.63268 6.99972 4.75993 7.05243 4.85375 7.14625L6 8.29313L9.14625 5.14625C9.19271 5.09979 9.24786 5.06294 9.30855 5.0378C9.36925 5.01266 9.43431 4.99972 9.5 4.99972C9.5657 4.99972 9.63075 5.01266 9.69145 5.0378C9.75215 5.06294 9.8073 5.09979 9.85375 5.14625C9.90021 5.1927 9.93706 5.24786 9.9622 5.30855C9.98734 5.36925 10.0003 5.4343 10.0003 5.5C10.0003 5.5657 9.98734 5.63075 9.9622 5.69145C9.93706 5.75214 9.90021 5.8073 9.85375 5.85375Z"
                          fill="#10b981"
                        />
                      </svg>
                      <span>
                        {translate("buyerPage.features.freeParkingDays")}{" "}
                        <strong className="text-white">{pkg.freeParkingDays || 0}</strong> days
                      </span>
                    </li>

                    <li className={`flex items-center gap-3 ${isRTL ? "flex-row-reverse" : ""}`}>
                      <svg width="20" height="20" viewBox="0 0 14 14" fill="none" className="flex-shrink-0">
                        <path
                          d="M7 0.5C5.71442 0.5 4.45772 0.881218 3.3888 1.59545C2.31988 2.30968 1.48676 3.32484 0.994786 4.51256C0.502816 5.70028 0.374095 7.00721 0.624899 8.26809C0.875703 9.52896 1.49477 10.6872 2.40381 11.5962C3.31285 12.5052 4.47104 13.1243 5.73192 13.3751C6.99279 13.6259 8.29973 13.4972 9.48744 13.0052C10.6752 12.5132 11.6903 11.6801 12.4046 10.6112C13.1188 9.54229 13.5 8.28558 13.5 7C13.4982 5.27665 12.8128 3.62441 11.5942 2.40582C10.3756 1.18722 8.72335 0.50182 7 0.5ZM9.85375 5.85375L6.35375 9.35375C6.30732 9.40024 6.25217 9.43712 6.19147 9.46228C6.13077 9.48744 6.06571 9.50039 6 9.50039C5.9343 9.50039 5.86923 9.48744 5.80853 9.46228C5.74783 9.43712 5.69269 9.40024 5.64625 9.35375L4.14625 7.85375C4.05243 7.75993 3.99972 7.63268 3.99972 7.5C3.99972 7.36732 4.05243 7.24007 4.14625 7.14625C4.24007 7.05243 4.36732 6.99972 4.5 6.99972C4.63268 6.99972 4.75993 7.05243 4.85375 7.14625L6 8.29313L9.14625 5.14625C9.19271 5.09979 9.24786 5.06294 9.30855 5.0378C9.36925 5.01266 9.43431 4.99972 9.5 4.99972C9.5657 4.99972 9.63075 5.01266 9.69145 5.0378C9.75215 5.06294 9.8073 5.09979 9.85375 5.14625C9.90021 5.1927 9.93706 5.24786 9.9622 5.30855C9.98734 5.36925 10.0003 5.4343 10.0003 5.5C10.0003 5.5657 9.98734 5.63075 9.9622 5.69145C9.93706 5.75214 9.90021 5.8073 9.85375 5.85375Z"
                          fill="#10b981"
                        />
                      </svg>
                      <span>
                        {translate("buyerPage.features.fineAfterFreeParking")}{" "}
                        <strong className="text-white">
                          {displayPriceWithSymbol(convertPrice(pkg.fineAfterFreeParkingDays || 0))}
                        </strong>
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              <button
                onClick={() => handlePackageRequest(pkg)}
                disabled={isRequesting}
                className={`
                  mt-auto w-full py-5 text-lg font-medium transition-all
                  bg-primary hover:bg-primary-dark text-white
                  disabled:opacity-50 disabled:cursor-not-allowed
                `}
              >
                {isRequesting
                  ? translate("buyerPage.buttons.requesting") || "Requesting..."
                  : translate("buyerPage.buttons.requestPackage") || "Request Package"}
              </button>
            </motion.div>
          ))}
        </motion.div>

        {/* Navigation Arrows */}
        {packages.length > cardsPerPage && (
          <div className="max-w-7xl mx-auto px-6 mt-10 flex justify-end gap-4">
            <button
              className={`p-4 rounded-full transition-all ${
                startIndex === 0
                  ? "bg-gray-800 text-gray-500 cursor-not-allowed"
                  : "bg-gray-700 text-white hover:bg-gray-600 hover:scale-110"
              }`}
              onClick={handleLeft}
              disabled={startIndex === 0}
              aria-label="Previous packages"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>

            <button
              className={`p-4 rounded-full transition-all ${
                startIndex + cardsPerPage >= packages.length
                  ? "bg-gray-800 text-gray-500 cursor-not-allowed"
                  : "bg-gray-700 text-white hover:bg-gray-600 hover:scale-110"
              }`}
              onClick={handleRight}
              disabled={startIndex + cardsPerPage >= packages.length}
              aria-label="Next packages"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmDialog && selectedPackage && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000] p-4 backdrop-blur-sm">
          <motion.div
            className="bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto border border-slate-700"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {/* Modal Header */}
            <div className={`flex justify-between items-center p-6 border-b border-slate-700 ${isRTL ? "flex-row-reverse" : ""}`}>
              <h3 className="text-2xl font-bold text-white">
                {translate("buyerPackages.modal.requestPackageConfirmation") || "Confirm Package Request"}
              </h3>
              <button
                onClick={closeConfirmDialog}
                className="text-slate-400 hover:text-white transition-colors text-2xl"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 ${isRTL ? "lg:grid-flow-col-dense" : ""}`}>
                {/* Left Column: Warning & Notice */}
                <div className={`space-y-6 ${isRTL ? "lg:order-2" : "lg:order-1"}`}>
                  <div className="bg-red-900/30 border border-red-700/50 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <svg className="w-7 h-7 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <h4 className={`text-xl font-bold text-red-300 ${isRTL ? "mr-3" : "ml-3"}`}>
                        {translate("buyerPackages.modal.importantNotice") || "Important Notice"}
                      </h4>
                    </div>

                    <p className={`text-slate-300 mb-5 ${isRTL ? "text-right" : "text-left"}`}>
                      {translate("buyerPackages.modal.noticeMessage") ||
                        "Your request will be processed after admin approval. Please ensure payment is made correctly."}
                    </p>

                    <ul className={`space-y-3 text-slate-300 ${isRTL ? "space-y-reverse" : ""}`}>
                      <li className={`flex items-start gap-3 ${isRTL ? "flex-row-reverse" : ""}`}>
                        <span className="text-red-400 text-xl">•</span>
                        <span>{translate("buyerPackages.modal.packageWillBeActivated") || "Package will be activated only after admin approval"}</span>
                      </li>
                      <li className={`flex items-start gap-3 ${isRTL ? "flex-row-reverse" : ""}`}>
                        <span className="text-red-400 text-xl">•</span>
                        <span>{translate("buyerPackages.modal.uploadClearReceipt") || "Upload a clear image of your payment receipt"}</span>
                      </li>
                      <li className={`flex items-start gap-3 ${isRTL ? "flex-row-reverse" : ""}`}>
                        <span className="text-red-400 text-xl">•</span>
                        <span>{translate("buyerPackages.modal.waitForApproval") || "Wait for admin approval (usually within 24-48 hours)"}</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Right Column: Package Info + Upload */}
                <div className={`space-y-6 ${isRTL ? "lg:order-1" : "lg:order-2"}`}>
                  <div>
                    <h4 className={`text-xl font-semibold text-white mb-4 ${isRTL ? "text-right" : "text-left"}`}>
                      {translate("buyerPackages.modal.packageDetails") || "Package Details"}
                    </h4>
                    <div className="bg-slate-800 rounded-xl p-6 space-y-4 border border-slate-700">
                      <div className="flex justify-between">
                        <span className="text-slate-400">{translate("buyerPackages.labels.packageName") || "Package"}:</span>
                        <span className="text-white font-medium">{selectedPackage.name}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-slate-400 block text-sm">{translate("buyerPackages.labels.depositRequired") || "Deposit Required"}:</span>
                          <span className="text-white font-medium">
                            {displayPriceWithSymbol(convertPrice(selectedPackage.deposit || 0))}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-400 block text-sm">{translate("buyerPackages.labels.biddingLimit") || "Bidding Limit"}:</span>
                          <span className="text-white font-medium">
                            {displayPriceWithSymbol(convertPrice(selectedPackage.creditLimit || 0))}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-400 block text-sm">{translate("buyerPackages.labels.freeParking") || "Free Parking"}:</span>
                          <span className="text-white font-medium">
                            {selectedPackage.freeParkingDays || 0} {translate("buyerPackages.labels.days") || "days"}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-400 block text-sm">{translate("buyerPackages.labels.carLimit") || "Car Limit"}:</span>
                          <span className="text-white font-medium">
                            {selectedPackage.carLimit || "Unlimited"} {translate("buyerPackages.labels.cars") || "cars"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Receipt Upload */}
                  <div>
                    <label className={`block text-sm font-medium text-slate-300 mb-3 ${isRTL ? "text-right" : "text-left"}`}>
                      {translate("buyerPackages.modal.paymentReceiptImage") || "Payment Receipt Image"}{" "}
                      <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className={`
                          block w-full text-sm text-slate-400
                          file:mr-4 file:py-3 file:px-6
                          file:rounded-xl file:border-0
                          file:text-sm file:font-semibold
                          file:bg-slate-700 file:text-white
                          hover:file:bg-slate-600
                          cursor-pointer
                          ${isRTL ? "file:ml-4 file:mr-0" : ""}
                        `}
                        required
                      />
                    </div>
                    <p className={`text-xs text-slate-500 mt-2 ${isRTL ? "text-right" : "text-left"}`}>
                      {translate("buyerPackages.modal.uploadReceiptMessage") ||
                        "Please upload a clear photo of your payment receipt"}
                    </p>

                    {imagePreview && (
                      <div className="mt-5">
                        <label className={`block text-sm font-medium text-slate-300 mb-3 ${isRTL ? "text-right" : "text-left"}`}>
                          {translate("buyerPackages.modal.imagePreview") || "Image Preview"}
                        </label>
                        <div className="border-2 border-slate-700 rounded-xl overflow-hidden bg-slate-950 p-3">
                          <img
                            src={imagePreview}
                            alt="Receipt preview"
                            className="max-h-56 mx-auto object-contain rounded-lg"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className={`flex flex-col sm:flex-row gap-4 pt-6 ${isRTL ? "flex-row-reverse" : ""}`}>
                    <button
                      onClick={closeConfirmDialog}
                      disabled={isRequesting}
                      className="flex-1 px-6 py-3.5 bg-slate-800 text-slate-300 rounded-xl hover:bg-slate-700 transition disabled:opacity-50 font-medium"
                    >
                      {translate("buyerPackages.modal.cancel") || "Cancel"}
                    </button>
                    <button
                      onClick={confirmAndRequestPackage}
                      disabled={isRequesting || !receiptImage}
                      className="flex-1 px-6 py-3.5 bg-primary text-white rounded-xl hover:bg-primary-dark transition disabled:opacity-50 font-bold shadow-lg shadow-primary/20"
                    >
                      {isRequesting
                        ? translate("buyerPage.buttons.requesting") || "Requesting..."
                        : translate("buyerPackages.modal.confirmRequest") || "Confirm Request"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </section>
  );
}